'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FormProps } from 'tdesign-react';
import { Form, MessagePlugin } from 'tdesign-react';
import { useLocalGlobalStore, useMedia } from '@ssv-website/hooks';
import { I18nProvider } from '../i18n/I18nProvider';
import { getLanguageSwitchMeta } from '../i18n/config';
import { isAvatarUploadReady } from './AvatarUploadField';
import {
  CONFERENCE_DUPLICATE_REGISTER_MESSAGE,
  getConferenceGuestDetail,
  registerConference2026,
  updateConferenceGuest,
} from './api';
import { DesktopSignupPage } from './DesktopSignupPage';
import {
  buildConferenceRegisterPayload,
  buildGuestDetailInitialValues,
  buildStaticInitialValues,
  formatMaskedPhone,
  isConferenceRegisterPayloadChanged,
  isSignupFormSubmittable,
  mergeInitialValues,
} from './form-helpers';
import { MobileSignupPage } from './MobileSignupPage';
import type {
  ConferenceGuestDetail,
  SignupFormValues,
  SignupPageProps,
} from './types';
import './desktop.css';
import './mobile.css';

const SUBMIT_LOGIN_REQUIRED_MESSAGE = '请先登录后再提交';
const AVATAR_UPLOAD_REQUIRED_MESSAGE = '请先完成头像上传';
const SUBMIT_SUCCESS_MESSAGE = '提交成功';
const PROFILE_UPDATE_SUBMIT_SUCCESS_MESSAGE = '提交成功，等待审核中';
const PROFILE_UPDATE_NO_CHANGE_MESSAGE = '信息未修改，无需提交审核';
const EDIT_BUTTON_TEXT = '编辑信息';
const PROFILE_UPDATE_REVIEWING_BUTTON_TEXT = '更新信息审核中';
const PROFILE_UPDATE_REVIEW_STATUS_PENDING = 1;
const PROFILE_UPDATE_REVIEW_STATUS_APPROVED = 2;
const DESKTOP_SUBMIT_BUTTON_TEXT = '确认提交';
const MOBILE_SUBMIT_BUTTON_TEXT = '提交';

type ProfileUpdateReviewState = Pick<
  ConferenceGuestDetail,
  'hasPendingProfileUpdate' | 'profileUpdateReviewStatus'
>;

const getProfileUpdateReviewStatus = (
  detail?: ProfileUpdateReviewState | null
) =>
  detail?.hasPendingProfileUpdate
    ? PROFILE_UPDATE_REVIEW_STATUS_PENDING
    : detail?.profileUpdateReviewStatus;

// 与会聊个人信息编辑页保持一致：审核中 1、待发布 2 均不可继续编辑。
const isProfileUpdateBlocked = (detail?: ProfileUpdateReviewState | null) => {
  const reviewStatus = getProfileUpdateReviewStatus(detail);
  return (
    reviewStatus === PROFILE_UPDATE_REVIEW_STATUS_PENDING ||
    reviewStatus === PROFILE_UPDATE_REVIEW_STATUS_APPROVED
  );
};

function SignupPageLoading({ isMobile }: { isMobile: boolean }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <span
        aria-hidden="true"
        className={`inline-block animate-spin rounded-full border-[#0052D9]/20 border-t-[#0052D9] ${
          isMobile ? 'h-5 w-5 border-2' : 'h-7 w-7 border-[3px]'
        }`}
      />
    </div>
  );
}

export const SSVMeeting2026SignupPage = ({
  heroBackgroundSrc,
  heroIllustration,
  heroIllustrationAlt = '',
  currentLang = 'zh',
  enableLanguageSwitch = false,
  initialIsMobile = false,
}: SignupPageProps) => {
  const router = useRouter();
  const isConferenceChatEditEntry =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('from') ===
      'conference-chat';
  const [form] = Form.useForm();
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingSubmittedRegistration, setIsEditingSubmittedRegistration] =
    useState(false);
  // 是否刚提交成功（用于控制提交成功标题的显示）
  const [isSubmitSuccessState, setIsSubmitSuccessState] = useState(false);
  const [isProfileUpdateSubmitSuccess, setIsProfileUpdateSubmitSuccess] =
    useState(false);
  const [hasLocalProfileUpdatePending, setHasLocalProfileUpdatePending] =
    useState(false);
  const [guestDetail, setGuestDetail] = useState<ConferenceGuestDetail | null>(
    null
  );
  const [isLoadingGuestDetail, setIsLoadingGuestDetail] = useState(false);
  const [guestDetailRequestStatus, setGuestDetailRequestStatus] = useState<
    'idle' | 'loading' | 'done'
  >('idle');
  const isLogin = useLocalGlobalStore((state) => state.is_login);
  const isEditingFromClickRef = useRef(false);
  const userName = useLocalGlobalStore((state) => state.userName);
  const info = useLocalGlobalStore((state) => state.info) as Record<
    string,
    unknown
  >;
  const currentIdentity = useLocalGlobalStore(
    (state) => state.currentIdentity
  ) as { teamType?: number };
  const hasTeam = useLocalGlobalStore((state) => state.hasTeam);
  const mediaIsMobile = useMedia('mobile');
  const isMobile = mediaIsMobile ?? initialIsMobile;
  const userGuid = String(info.GUID ?? '');
  const hasExistingRegistration = Boolean(guestDetail?.registerId);
  const hasPendingProfileUpdate =
    hasLocalProfileUpdatePending || isProfileUpdateBlocked(guestDetail);
  const isProfileUpdateMode =
    isConferenceChatEditEntry && hasExistingRegistration;
  const isEditingExistingRegistration =
    hasExistingRegistration && isEditingSubmittedRegistration;
  const isReadonlySubmittedState =
    hasExistingRegistration && !isEditingSubmittedRegistration;

  const getResolvedFormValues = (values?: Partial<SignupFormValues>) =>
    mergeInitialValues(
      buildStaticInitialValues(userName, info),
      guestDetail ? buildGuestDetailInitialValues(guestDetail) : undefined,
      values
    );

  const getInitialResolvedFormValues = () =>
    mergeInitialValues(
      buildStaticInitialValues(userName, info),
      guestDetail ? buildGuestDetailInitialValues(guestDetail) : undefined
    );

  const getCurrentResolvedFormValues = () =>
    getResolvedFormValues(
      (form.getFieldsValue(true) as Partial<SignupFormValues>) ?? {}
    );

  const syncSubmitState = () => {
    const resolvedValues = getCurrentResolvedFormValues();

    setIsSubmitDisabled(!isSignupFormSubmittable(resolvedValues));
  };

  useLayoutEffect(() => {
    const initialValues = getResolvedFormValues();

    form.setFieldsValue(initialValues);
    form.clearValidate();
    setIsSubmitDisabled(!isSignupFormSubmittable(initialValues));

    const timer = window.setTimeout(() => {
      form.clearValidate();
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, [form, guestDetail, info, userName, isEditingSubmittedRegistration]);

  useEffect(() => {
    let isCancelled = false;

    if (!isLogin) {
      setGuestDetail(null);
      setIsLoadingGuestDetail(false);
      setIsEditingSubmittedRegistration(false);
      setGuestDetailRequestStatus('done');
      return () => {
        isCancelled = true;
      };
    }

    const loadGuestDetail = async () => {
      setGuestDetail(null);
      setIsLoadingGuestDetail(true);
      setGuestDetailRequestStatus('loading');

      try {
        const response = await getConferenceGuestDetail();
        if (isCancelled) {
          return;
        }

        if (response.code === 0 && response.data) {
          setGuestDetail(response.data);
          if (
            isConferenceChatEditEntry &&
            !isProfileUpdateBlocked(response.data)
          ) {
            // 会聊个人资料入口进入后应直接展示可编辑表单，提交时由后端创建资料更新审核单。
            isEditingFromClickRef.current = true;
            setIsEditingSubmittedRegistration(true);
          }
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('获取报名详情失败', error);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingGuestDetail(false);
          setGuestDetailRequestStatus('done');
        }
      }
    };

    loadGuestDetail();

    return () => {
      isCancelled = true;
    };
  }, [isConferenceChatEditEntry, isLogin, userGuid]);

  const onPrimaryAction = () => {
    if (isReadonlySubmittedState) {
      isEditingFromClickRef.current = true;
      setIsEditingSubmittedRegistration(true);
      setIsSubmitSuccessState(false); // 进入编辑状态时重置提交成功状态
      setIsProfileUpdateSubmitSuccess(false);
      setHasLocalProfileUpdatePending(false);
      window.scrollTo({ top: 0, behavior: 'smooth' }); // 滚动到顶部
      syncSubmitState();
      return;
    }

    form.submit();
  };

  const onSubmit: FormProps['onSubmit'] = async (context) => {
    const submitValues = getCurrentResolvedFormValues();

    if (context.firstError) {
      return;
    }

    if (!isLogin) {
      MessagePlugin.warning({
        content: SUBMIT_LOGIN_REQUIRED_MESSAGE,
        duration: 2000,
      });
      return;
    }

    if (isReadonlySubmittedState) {
      return;
    }

    if (!isAvatarUploadReady(submitValues.avatar)) {
      MessagePlugin.warning({
        content: AVATAR_UPLOAD_REQUIRED_MESSAGE,
        duration: 2000,
      });
      return;
    }

    const finalEmail =
      submitValues.email || String(info.Email || info.email || '');
    const payload = buildConferenceRegisterPayload(submitValues, finalEmail);

    if (isProfileUpdateMode && guestDetail) {
      const initialValues = getInitialResolvedFormValues();
      const initialPayload = buildConferenceRegisterPayload(
        initialValues,
        initialValues.email || String(info.Email || info.email || '')
      );

      if (!isConferenceRegisterPayloadChanged(payload, initialPayload)) {
        MessagePlugin.info({
          content: PROFILE_UPDATE_NO_CHANGE_MESSAGE,
          duration: 2000,
        });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (
        (isEditingExistingRegistration || isProfileUpdateMode) &&
        guestDetail
      ) {
        await updateConferenceGuest(payload);
      } else {
        await registerConference2026(payload);
      }
      const submittedAsProfileUpdate = isProfileUpdateMode;

      // 提交成功后重新获取详情数据，确保获取服务器处理后的头像URL等字段
      const response = await getConferenceGuestDetail();
      if (response.code === 0 && response.data) {
        setGuestDetail(response.data);
      }

      setIsEditingSubmittedRegistration(false);
      setIsSubmitSuccessState(true); // 标记为提交成功状态
      setIsProfileUpdateSubmitSuccess(submittedAsProfileUpdate);
      // 会聊资料更新提交后由后端生成审核单，本地先锁定，避免详情接口缓存/延迟导致按钮短暂可点。
      setHasLocalProfileUpdatePending(submittedAsProfileUpdate);
      window.scrollTo({ top: 0, behavior: 'smooth' }); // 滚动到顶部
      MessagePlugin.success({
        content: submittedAsProfileUpdate
          ? PROFILE_UPDATE_SUBMIT_SUCCESS_MESSAGE
          : SUBMIT_SUCCESS_MESSAGE,
        duration: 2000,
      });
    } catch (error) {
      if (
        !isEditingExistingRegistration &&
        error instanceof Error &&
        error.message === CONFERENCE_DUPLICATE_REGISTER_MESSAGE
      ) {
        try {
          const response = await getConferenceGuestDetail();
          if (response.code === 0 && response.data) {
            setGuestDetail(response.data);
            setIsEditingSubmittedRegistration(false);
            setIsSubmitSuccessState(true); // 标记为提交成功状态
          }
        } catch {
          // noop
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCancel = () => {
    // 如果是从已提交信息页点击编辑进来的，取消后回到已提交信息页（只读状态）
    if (isEditingFromClickRef.current) {
      isEditingFromClickRef.current = false;
      setIsEditingSubmittedRegistration(false);
      return;
    }
    // 否则（登录后直接填写表单），回到首页
    router.push('/');
  };

  const displayedPhone = formatMaskedPhone(
    String(info.Mobile ?? info.Phone ?? '')
  );
  const { langLabel, targetLang } = getLanguageSwitchMeta(currentLang);
  const readonlyValues = getResolvedFormValues();
  const shouldShowGuestDetailPageLoading =
    isLogin && guestDetailRequestStatus !== 'done';
  const finalIsSubmitDisabled =
    isLoadingGuestDetail ||
    (hasPendingProfileUpdate
      ? true
      : isReadonlySubmittedState
      ? false
      : isSubmitDisabled);
  const primaryButtonText = hasPendingProfileUpdate
    ? PROFILE_UPDATE_REVIEWING_BUTTON_TEXT
    : isReadonlySubmittedState
    ? EDIT_BUTTON_TEXT
    : DESKTOP_SUBMIT_BUTTON_TEXT;
  const mobilePrimaryButtonText = hasPendingProfileUpdate
    ? PROFILE_UPDATE_REVIEWING_BUTTON_TEXT
    : isReadonlySubmittedState
    ? EDIT_BUTTON_TEXT
    : MOBILE_SUBMIT_BUTTON_TEXT;
  const submitSuccessTitle = isProfileUpdateSubmitSuccess
    ? PROFILE_UPDATE_SUBMIT_SUCCESS_MESSAGE
    : SUBMIT_SUCCESS_MESSAGE;

  if (shouldShowGuestDetailPageLoading) {
    return <SignupPageLoading isMobile={isMobile} />;
  }

  if (isMobile) {
    return (
      <I18nProvider defaultLocale={currentLang}>
        <MobileSignupPage
          displayedPhone={displayedPhone}
          form={form}
          heroBackgroundSrc={heroBackgroundSrc}
          heroIllustration={heroIllustration}
          heroIllustrationAlt={heroIllustrationAlt}
          isEnteringFromEdit={isEditingFromClickRef.current}
          isLogin={isLogin}
          isReadonlySubmittedState={isReadonlySubmittedState}
          isSubmitDisabled={finalIsSubmitDisabled}
          isSubmitting={isSubmitting}
          isSubmitSuccessState={isSubmitSuccessState}
          onPrimaryAction={onPrimaryAction}
          onSubmit={onSubmit}
          readonlyValues={readonlyValues}
          submitButtonText={mobilePrimaryButtonText}
          submitSuccessTitle={submitSuccessTitle}
          syncSubmitState={syncSubmitState}
        />
      </I18nProvider>
    );
  }

  return (
    <DesktopSignupPage
      displayedPhone={displayedPhone}
      enableLanguageSwitch={enableLanguageSwitch}
      form={form}
      hasTeam={hasTeam}
      heroBackgroundSrc={heroBackgroundSrc}
      isReadonlySubmittedState={isReadonlySubmittedState}
      isSubmitDisabled={finalIsSubmitDisabled}
      isSubmitting={isSubmitting}
      isSubmitSuccessState={isSubmitSuccessState}
      langLabel={langLabel}
      onCancel={onCancel}
      onPrimaryAction={onPrimaryAction}
      onSubmit={onSubmit}
      readonlyValues={readonlyValues}
      submitButtonText={primaryButtonText}
      submitSuccessTitle={submitSuccessTitle}
      teamType={currentIdentity.teamType}
      syncSubmitState={syncSubmitState}
      targetLang={targetLang}
    />
  );
};
